import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import withLoader from 'components/organisms/withLoader';
import ControlPanelButton from 'components/atoms/controlPanelButton';
import EditButton from 'components/atoms/editButton';
import MessageIcon from 'components/atoms/icons/messageIcon';
import GeneralCommentModal from 'components/templates/rpm/patients/patient/generalComments/modal';
import CallIcon from 'components/atoms/icons/callIcon';
import ConsentIcon from 'components/atoms/icons/consentIcon';
import SmsIcon from 'components/atoms/icons/smsIcon';
import Comments from 'components/templates/rpm/patients/patient/generalComments';
import ControlPanelButtonDropdown from 'components/atoms/controlPanelButton/dropdown';
import useCallButtons from 'utils/hooks/rpm/controlPanel/useCallButtons';
import { Row, Col, Table } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import {
  getPatientCcmStatus,
  getPatientPcmStatus,
  getPatientStatus,
  showResult,
} from 'utils';
import forms from 'constants/forms';
import {
  isBiller,
  isPractice,
  isCcmAgencyDoctor,
  isAdmin,
} from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import PatientStatusAndCovidStatus from 'components/atoms/patientStatus/patientStatusAndCovidStatus';
import PatientConsentStatus from 'components/atoms/patientStatus/patientConsentStatus';
import PatientHoldingStatus from 'components/atoms/patientStatus/patientHoldingStatus';
import CareAgents from 'components/molecules/careAgents/careAgents';
import {
  hasAdminPanelAccess,
  hasClinicalRole,
  hasPatientAcceptanceRole,
} from 'utils/accessManager/helpers';
import { isMobile } from 'utils/deviceHelper';
import SmsModal from 'components/templates/rpm/patients/patient/messages/modal';
import SmsModalMobile from 'components/templates/rpm/patients/patient/messages/modalMobile';
import InteractionStatusButton from 'components/molecules/controlPanel/interactionStatusButton';
import InternalAdmissionFormControlButton from 'components/templates/admin/internalAdmissonForm/InternalAdmissonFormButton';
import PersonalInfo from './components/personalInfo';
import AccountInfo from './components/accountInfo';
import Dynamic from './components/dynamic';
import Conditions from './components/conditions/conditions';
import PriorAuth from './components/priorAuth';
import Insurance from './components/insurance';
import Devices from './components/devices';
import ContactInfo from './components/contactInfo';
import Physician from './components/physician';
import Thresholds from './components/thresholds';
import PatientStatusLogs from './components/statusLogs';
import PatientCcmStatusLogs from './components/CcmStatusLogs';
import PatientHoldingStatusLogs from './components/holdingStatusLogs';
import ShortInfo from './components/shortInfo';
import ButtonsSection from './components/buttonsSection';
import PatientStatusButton from './components/patientStatusButton';
import PatientCcmStatusButton from './components/patientCcmStatusButton';
import PatientPcmStatusButton from './components/patientPcmStatusButton';
import ClinicRunBookLogs from './components/clinicRunBookLogs';

const Patient = () => {
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const { userType, roleNames } = useUser();
  const iamPatientAcceptance = hasPatientAcceptanceRole(roleNames);
  const showCommentButton = useMemo(
    () =>
      hasPatientAcceptanceRole(roleNames) ||
      hasAdminPanelAccess(roleNames) ||
      hasClinicalRole(roleNames) ||
      isAdmin(userType),
    [],
  );
  const [commentsPopupOpen, setCommentsPopupOpen] = useState(false);
  const { patientGuid } = useParams();
  const commentSubmitLoading = useSelector(
    (state) => state.loading.effects.comments.submit,
  );
  const device = useSelector((state) => state.user.deviceType);
  const smsTemplates = useSelector((state) => state.sms.templates);

  const { getTemplates: smsTemplatesLoading, submit: messageSubmitLoading } =
    useSelector((state) => state.loading.effects.sms);

  const {
    togglePatientInteractionStatus: updatePatientInteractionStatusLoading,
    togglePatientCcmInteractionStatus: updatePatientCcmInteractionStatusLoading,
    togglePvr: updatePatientPvrLiteLoading,
    toggleDoNotCall: updateDoNotCallLoading,
    getPatient: patientDetailsLoading,
  } = useSelector((state) => state.loading.effects.adminPanelPatients);

  const {
    comments: { submit: submitComment },
    sms: { getTemplates: getSmsTemplates, submit: submitSms },
    ccmPatientDetails: { getPatientDetails },
    patient: { getDetails },
    adminPanelPatients: {
      getPatient,
      reset: resetPatient,
      togglePatientInteractionStatus,
      togglePvr,
      toggleDoNotCall,
      togglePatientCcmInteractionStatus,
    },
  } = useDispatch();

  const patient = useSelector((state) => state.adminPanelPatients.patient);
  const rpmPatient = useSelector((state) => state.patient);
  const { doctor } = rpmPatient;
  const { profile } = rpmPatient;

  const updateInteractionStatus = async (type) => {
    let res = null;
    if (type === 'rpm') {
      res = await togglePatientInteractionStatus({
        patientGuid,
        isInteracted: !patient.profile.interactionStatus,
      });
    } else {
      res = await togglePatientCcmInteractionStatus({
        patientGuid,
        isInteracted: !patient.profile.ccmInteractionStatus,
      });
    }
    if (res?.error) showResult(res.error);
  };

  const loadPageData = async () => {
    getPatient({ patientGuid });
    await getDetails(patientGuid);
    getSmsTemplates();
  };

  const onAfterChangeStatus = async ({ reloadInternalAdmissionForm }) => {
    if (reloadInternalAdmissionForm) {
      await getPatient({ patientGuid });
    }
  };

  useEffect(() => {
    loadPageData();
    return () => {
      resetPatient();
    };
  }, []);

  useEffect(() => {
    if (
      !patient?.profile?.ccmStatus ||
      patient?.profile?.ccmStatus === 'not_enrolled'
    ) {
      return;
    }
    getPatientDetails(patientGuid);
  }, [patient?.profile?.ccmStatus]);

  const rmpStatus = patient?.profile?.status
    ? getPatientStatus(patient.profile.status)?.label || ''
    : '';

  const ccmStatus = patient?.profile?.ccmStatus
    ? getPatientCcmStatus(patient.profile.ccmStatus)?.label || ''
    : '';

  const pcmStatus = getPatientPcmStatus(patient?.profile?.pcmStatus);

  const updatePvrLiteState = async () => {
    const { error } = await togglePvr({
      patientGuid: patient.profile.guid,
      acceptPvrLite: !patient.profile.acceptPvrLite,
    });
    if (error) showResult(error);
  };

  const updateDoNotCallState = async () => {
    const { error } = await toggleDoNotCall({
      patientGuid: patient.profile.guid,
      doNotCall: !patient.profile.doNotCall,
    });
    if (error) showResult(error);
  };

  const rpmCallButtons = useCallButtons({
    doctor,
    patient,
    profile,
    userType,
    roleNames,
    isRpmPanel: true,
    patientGuid,
    patientDetailsLoading,
  });

  const ccmCallButtons = useCallButtons({
    doctor,
    patient,
    profile,
    userType,
    roleNames,
    isRpmPanel: false,
    patientGuid,
    patientDetailsLoading,
  });

  const ccmStatusOptionsByRole = iamPatientAcceptance
    ? forms.ccmStatusOptionsForPatientAcceptanceRole
    : forms.ccmStatusOptions;

  const ccmStatusOptions = useMemo(() => [...ccmStatusOptionsByRole]);

  return (
    <div className="admin-patient">
      <ShortInfo patientLoading={patientDetailsLoading} patient={patient} />
      <Row>
        <Col>
          <ButtonsSection title="Statuses">
            {withLoader(
              patientDetailsLoading,
              patient.profile && (
                <>
                  <PatientStatusButton
                    profile={patient.profile}
                    afterChangeStatus={onAfterChangeStatus}
                  />

                  <PatientCcmStatusButton
                    profile={patient.profile}
                    statusOptions={ccmStatusOptions}
                  />

                  {!isBiller(roleNames) && (
                    <PatientHoldingStatus
                      patientGuid={patient.profile.guid}
                      holdingStatus={patient.profile.holdingStatus}
                      patientDetailsLoading={patientDetailsLoading}
                      isAdminPage
                    />
                  )}

                  {!iamPatientAcceptance && (
                    <PatientStatusAndCovidStatus
                      profile={patient.profile}
                      patientDetailsLoading={patientDetailsLoading}
                    />
                  )}

                  <PatientConsentStatus
                    profile={patient.profile}
                    patientDetailsLoading={patientDetailsLoading}
                  />

                  {!iamPatientAcceptance && (
                    <div className="admin-patient-header-actions-call">
                      <ControlPanelButton
                        label="PVR Lite Opt In/Out"
                        bracketLabel={
                          patient.profile?.acceptPvrLite
                            ? 'Opted In'
                            : 'Opted out'
                        }
                        patientDetailsLoading={patientDetailsLoading}
                        icon={ConsentIcon}
                        disabled={updatePatientPvrLiteLoading}
                        handleClick={updatePvrLiteState}
                      />
                    </div>
                  )}

                  {!iamPatientAcceptance && (
                    <div className="admin-patient-header-actions-call">
                      <ControlPanelButton
                        label={'Toggle "Do Not Call" Flag'}
                        bracketLabel={patient.profile?.doNotCall ? 'Yes' : 'No'}
                        patientDetailsLoading={patientDetailsLoading}
                        icon={CallIcon}
                        disabled={updateDoNotCallLoading}
                        handleClick={updateDoNotCallState}
                      />
                    </div>
                  )}

                  <PatientPcmStatusButton
                    profile={patient.profile}
                    statusOptions={ccmStatusOptions}
                  />
                </>
              ),
            )}
          </ButtonsSection>
        </Col>
        <Col>
          <ButtonsSection title="Interaction">
            {showCommentButton && (
              <div className="admin-patient-header-actions-call">
                <ControlPanelButton
                  label="General Comment"
                  icon={MessageIcon}
                  handleClick={() => {
                    setCommentsPopupOpen(true);
                  }}
                />
              </div>
            )}

            {!isMobile(device) && (
              <>
                <div className="control-panel-buttons admin-patient-header-actions-call">
                  <ControlPanelButtonDropdown
                    label="RPM Call"
                    icon={CallIcon}
                    buttons={rpmCallButtons}
                  />
                </div>

                {(isCcmAgencyDoctor(userType, roleNames) ||
                  isPractice(userType) ||
                  isAdmin(userType)) && (
                  <div className="control-panel-buttons admin-patient-header-actions-call">
                    <ControlPanelButtonDropdown
                      label="CCM Call"
                      icon={CallIcon}
                      buttons={ccmCallButtons}
                    />
                  </div>
                )}

                <div className="admin-patient-header-actions-call">
                  <ControlPanelButton
                    label="SMS Patient"
                    icon={SmsIcon}
                    handleClick={() => setSmsPopupOpen(true)}
                  />
                </div>
                <div className="admin-patient-header-actions-call">
                  <InteractionStatusButton
                    label="RPM Monthly Interaction (Phone/SMS)"
                    status={patient.profile?.interactionStatus}
                    loading={
                      updatePatientInteractionStatusLoading ||
                      patientDetailsLoading
                    }
                    handleClick={() => updateInteractionStatus('rpm')}
                  />
                </div>
                <div className="admin-patient-header-actions-call">
                  <InteractionStatusButton
                    label="Follow-up/Appt Complete"
                    status={patient.profile?.ccmInteractionStatus}
                    loading={
                      updatePatientCcmInteractionStatusLoading ||
                      patientDetailsLoading
                    }
                    handleClick={() => updateInteractionStatus('ccm')}
                  />
                </div>
                <div className="admin-patient-header-actions-call">
                  <InternalAdmissionFormControlButton
                    patient={patient}
                    loading={patientDetailsLoading}
                  />
                </div>
              </>
            )}
          </ButtonsSection>
        </Col>
      </Row>
      {withLoader(
        patientDetailsLoading,
        <>
          {patient.profile && (
            <>
              <div className="admin-patient-header">
                <span className="admin-patient-header-span">Patient Info</span>
                <div className="admin-patient-header-actions">
                  <div className="d-flex">
                    <div className="admin-patient-header-actions-edit">
                      <Link
                        to={`/admin/patient/${patient.profile.guid}/edit`}
                        data-cy="admin-patient__edit-link"
                      >
                        <EditButton />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-user-profile-block admin-patient-block">
                <Row>
                  <Col>
                    <PersonalInfo profile={patient.profile} />
                    <AccountInfo
                      profile={patient.profile}
                      rpmStatus={rmpStatus}
                      ccmStatus={ccmStatus}
                      pcmStatus={pcmStatus}
                    />
                    <Dynamic diagnosis={patient.diagnosis} emr={patient.emr} />
                    <Conditions patientDetailsLoading={false} adminPage />
                    <PriorAuth priorAuths={patient.priorAuths} />
                    <Insurance insurances={patient.insurances} />
                    <PatientStatusLogs patientGuid={patientGuid} adminPage />
                    <PatientCcmStatusLogs patientGuid={patientGuid} adminPage />
                    {!isBiller(roleNames) && (
                      <PatientHoldingStatusLogs
                        patientGuid={patientGuid}
                        adminPage
                      />
                    )}
                  </Col>
                  <Col>
                    <ContactInfo profile={patient.profile} />
                    <Physician
                      doctor={patient.doctor}
                      clinic={patient.clinic}
                    />
                    <ClinicRunBookLogs clinicGuid={patient.clinic.clinicGuid} />
                    <CareAgents
                      agents={patient.careAgents}
                      label="Care Agents"
                      displayProfType
                    />
                    <Thresholds thresholds={patient.thresholds} />
                    <Devices devices={patient.devices} adminPage />
                    <div>
                      <h5>Device Order Specifications</h5>
                      <Table
                        borderless
                        border="0"
                        cellSpacing="0"
                        cellPadding="0"
                      >
                        <thead>
                          <tr>
                            <th>Specifications</th>
                          </tr>
                        </thead>
                        <tr>
                          <td>
                            <div className="patient-status-logs">
                              <tbody className="admin-patient-device-order">
                                {patient.profile.deviceOrderSpecifications}
                              </tbody>
                            </div>
                          </td>
                        </tr>
                      </Table>
                    </div>
                    <Comments patientGuid={patientGuid} isAdminPanel />
                  </Col>
                </Row>
              </div>
            </>
          )}
        </>,
      )}
      <GeneralCommentModal
        isOpen={commentsPopupOpen}
        toggle={setCommentsPopupOpen}
        patientGuid={patientGuid}
        loading={commentSubmitLoading}
        submit={submitComment}
        isAdminPanel
      />
      {isMobile(device) && (
        <SmsModalMobile
          isOpen={smsPopupOpen}
          toggle={setSmsPopupOpen}
          patientGuid={patientGuid}
          smsTemplates={smsTemplates}
          loading={smsTemplatesLoading || messageSubmitLoading}
          submit={submitSms}
          isRpmPanel
        />
      )}
      {!isMobile(device) && (
        <SmsModal
          isOpen={smsPopupOpen}
          toggle={setSmsPopupOpen}
          patientGuid={patientGuid}
          smsTemplates={smsTemplates}
          loading={smsTemplatesLoading || messageSubmitLoading}
          submit={submitSms}
          isRpmPanel
        />
      )}
    </div>
  );
};

export default Patient;
